import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Seo from '../components/Seo';
import ArcadeGameCard from '../components/ArcadeGameCard';
import apiClient from '../services/api';
import { Loader2, Gamepad2, Play } from 'lucide-react';

export default function CreatorProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    apiClient
      .get(`/arcade/creators/${encodeURIComponent(username)}`)
      .then((res) => setProfile(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading creator...
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex flex-col items-center justify-center pt-40 text-gray-300">
          <p className="mb-4">Creator not found</p>
          <Link to="/games" className="px-4 py-2 rounded bg-purple-600 text-white">
            Browse the Arcade
          </Link>
        </div>
      </div>
    );
  }

  const joined = profile.joined_at ? new Date(profile.joined_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title={`${profile.username} – Game Creator | GameOS`}
        description={`Play ${profile.total_games} free AI-built games by ${profile.username} on GameOS — ${profile.total_plays.toLocaleString()} total plays. Browse their portfolio and remix any game.`}
        path={`/creator/${encodeURIComponent(profile.username)}`}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-12" data-testid="creator-profile-header">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <span className="text-white font-bold text-4xl">{profile.username.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white mb-1" data-testid="creator-username">{profile.username}</h1>
            <p className="text-gray-400 text-sm mb-3">{joined ? `Creating on GameOS since ${joined}` : 'GameOS creator'}</p>
            <div className="flex gap-3">
              <span className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full border border-purple-500/50 bg-purple-900/40 text-purple-100">
                <Gamepad2 size={14} /> {profile.total_games} games
              </span>
              <span className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full border border-pink-500/50 bg-pink-900/40 text-pink-100">
                <Play size={14} /> {profile.total_plays.toLocaleString()} plays
              </span>
            </div>
          </div>
        </div>

        {profile.games.length === 0 ? (
          <div className="text-center py-24 text-gray-400">This creator hasn't published any games yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="creator-games-grid">
            {profile.games.map((g) => (
              <ArcadeGameCard key={g.game_id} game={g} showCreator={false} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
